import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useLanguage } from "@/contexts/LanguageContext";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { User, LayoutDashboard, Settings, Crown, LogOut, MessageSquare, HelpCircle } from "lucide-react";

import { API, BACKEND_URL } from "@/lib/api";

export default function GlobalHeader({ user, onLogin, onLogout, showDashboard = false }) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [logo, setLogo] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    fetchLogo();
    if (user) {
      fetchUnreadCount();
    }
  }, [user]);

  const fetchLogo = async () => {
    try {
      const response = await axios.get(`${API}/branding/logo`);
      if (response.data && response.data.logo_url) {
        setLogo(`${BACKEND_URL || ""}${response.data.logo_url}`);
      }
    } catch (error) {
      console.error("Failed to fetch logo:", error);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const response = await axios.get(`${API}/user/messages/unread-count`, { withCredentials: true });
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error("Failed to fetch unread count:", error);
    }
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-[#121212]/80 backdrop-blur-3xl border-b border-white/5 font-['Plus_Jakarta_Sans',_sans-serif]">
      <div className="container mx-auto px-6 py-5 flex justify-between items-center text-white">
        <a
          href="/"
          className="flex items-center gap-2 hover:scale-[1.02] transition-transform cursor-pointer"
        >
          {logo ? (
            <img
              src={logo}
              alt="Mokiplaner"
              className="h-8 md:h-10 w-auto object-contain"
              style={{ transform: 'scale(var(--logo-scale, 1))' }}
            />
          ) : (
            <img
              src="/assets/moki-logo.svg"
              alt="MOKI PLANER"
              className="h-8 md:h-10 w-auto object-contain"
              style={{ transform: 'scale(var(--logo-scale, 1))' }}
              onError={(e) => {
                e.target.style.display = 'none';
                if (e.target.nextElementSibling) {
                  e.target.nextElementSibling.style.display = 'flex';
                }
              }}
            />
          )}
          <div className="hidden items-center gap-3">
            <div className="w-10 h-10 bg-brand-accent rounded-xl flex items-center justify-center text-black font-black">M</div>
            <span className="text-xl font-black tracking-tighter uppercase">Mokiplaner</span>
          </div>
        </a>
        <div className="flex items-center gap-2 md:gap-4 shrink-0">
          <LanguageSwitcher />

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="w-12 h-12 rounded-2xl relative overflow-hidden ring-1 ring-white/10 hover:ring-brand-accent transition-all shadow-2xl bg-white/5"
                >
                  {user.picture ? (
                    <img
                      src={user.picture}
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-white/40 font-black text-xs">
                      {user.name?.[0] || "U"}
                    </div>
                  )}
                  {unreadCount > 0 && (
                    <span className="absolute top-1 right-1 w-3 h-3 bg-brand-accent border-2 border-[#121212] rounded-full animate-pulse shadow-[0_0_10px_var(--brand-accent)]" />
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64 bg-brand-text border-white/10 text-white rounded-3xl p-2 shadow-[0_20px_50px_rgba(0,0,0,1)]">
                <div className="px-4 py-3 border-b border-white/5 mb-2">
                  <p className="font-black text-sm tracking-tight">{user.name}</p>
                  <p className="text-[10px] font-bold text-white/40 truncate uppercase tracking-widest mt-1">{user.email}</p>
                </div>

                {showDashboard && (
                  <DropdownMenuItem onClick={() => navigate("/dashboard")} className="cursor-pointer rounded-2xl focus:bg-brand-accent/15 focus:text-brand-accent py-3">
                    <LayoutDashboard className="w-4 h-4 mr-3" />
                    Dashboard
                  </DropdownMenuItem>
                )}

                <DropdownMenuItem onClick={() => navigate("/profile")} className="cursor-pointer rounded-2xl focus:bg-brand-accent/15 focus:text-brand-accent py-3">
                  <User className="w-4 h-4 mr-3" />
                  Mein Account
                </DropdownMenuItem>

                <DropdownMenuItem onClick={() => navigate("/profile?tab=messages")} className="cursor-pointer rounded-2xl focus:bg-brand-accent/15 focus:text-brand-accent py-3">
                  <MessageSquare className="w-4 h-4 mr-3" />
                  Postfach
                  {unreadCount > 0 && (
                    <span className="ml-auto text-[10px] bg-brand-accent text-black px-2 py-0.5 rounded-full font-black">
                      {unreadCount}
                    </span>
                  )}
                </DropdownMenuItem>

                <DropdownMenuItem onClick={() => navigate("/pricing")} className="cursor-pointer rounded-2xl focus:bg-brand-accent/15 focus:text-brand-accent py-3">
                  <Crown className="w-4 h-4 mr-3" />
                  Pakete & Preise
                </DropdownMenuItem>

                {(user.is_admin || user.role === 'admin') && (
                  <DropdownMenuItem onClick={() => navigate("/admin")} className="cursor-pointer rounded-2xl focus:bg-brand-accent/15 focus:text-brand-accent py-3 border-t border-white/5 mt-1">
                    <Settings className="w-4 h-4 mr-3" />
                    Verwaltung
                  </DropdownMenuItem>
                )}

                <DropdownMenuSeparator className="bg-white/5 mx-2" />

                {onLogout && (
                  <DropdownMenuItem
                    onClick={onLogout}
                    className="cursor-pointer text-rose-500 focus:text-rose-400 focus:bg-rose-500/10 rounded-2xl py-3"
                  >
                    <LogOut className="w-4 h-4 mr-3" />
                    Abmelden
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : onLogin ? (
            <Button
              onClick={onLogin}
              className="rounded-full bg-white text-black hover:bg-brand-accent hover:text-white transition-all font-black uppercase text-[10px] tracking-widest px-8"
              size="sm"
            >
              {t('nav.login')}
            </Button>
          ) : null}
        </div>
      </div>
    </nav>
  );
}
